import dynamic from 'next/dynamic';
import type { ReactElement } from 'react';
import React from 'react';

import CustomMetatag from '@/components/layouts/custom-metatag';
import Layout from '@/components/layouts/lcv';
import { BatteryTable } from '@/components/sections/service/part-detail/battery-table';
import { DescriptionTable } from '@/components/sections/service/part-detail/description-table';
import { RelatedParts } from '@/components/sections/service/part-detail/related-parts';
import { SymptomsTable } from '@/components/sections/service/part-detail/symptoms-table';
import { TireTable } from '@/components/sections/service/part-detail/tire-table';
import { Breadcrumb } from '@/components/shared/navigation/breadcrumb';
import { BATTERY_TIRE } from '@/const/part-item';
import {
  BUTTON_LCV_FIND_SERVICE_CENTER,
  MENU_LCV_SERVICE_PARTS,
} from '@/contents/lcv/link';
import {
  getPartsByCategory,
  getPartsByConfig,
  getPartsItemByUrl,
} from '@/services/contentstack';
import type { IPartDetail } from '@/types/shared/parts';

const FooterLCVGroup = dynamic(
  () => import('@/components/layouts/lcv/footer-group'),
  {
    ssr: false,
  }
);
export async function getStaticPaths() {
  const res = await getPartsByConfig('lcv');
  const items = res?.data?.entries ?? [];

  const paths = items.flatMap((o) =>
    (o?.parts_item ?? []).map((item) => ({
      params: {
        url: item?.url ?? '',
        category: o?.parts_category?.[0]?.url ?? '',
      },
    }))
  );

  return {
    paths,
    fallback: false,
  };
}

export async function getStaticProps({ params }) {
  const res = await getPartsItemByUrl(params?.url, 'lcv');
  const entryData = res?.data?.entries?.[0];

  if (!entryData) {
    return { redirect: { destination: `/service/parts` } };
  }

  const entry: IPartDetail = {
    metatag: {
      title: entryData?.metatag?.title,
      description: entryData?.metatag?.description,
      imgURL: entryData?.metatag?.img?.url || entryData?.item_image?.url || '',
    },
    uid: entryData?.uid,
    title: entryData?.headline_title,
    vehicleType: entryData?.vehicle_type,
    url: entryData?.url,
    category: entryData?.category?.[0] && {
      uid: entryData?.category[0]?.uid,
      name: entryData?.category[0]?.name,
      url: entryData?.category[0]?.url,
    },
    itemImage: { url: entryData?.item_image?.url || '' },
    isImageFullHeight: true,
    detailImage: {
      url: entryData?.detail_image?.url || '',
    },
    partItemFile: { src: entryData?.part_item_file?.url || '' },
    showDealerButton: entryData?.show_dealer_button || false,
    dealerButton: {
      label: 'ติดต่อศูนย์บริการ',
      url: BUTTON_LCV_FIND_SERVICE_CENTER.url,
    },
    partDescription: entryData?.part_description || '',
    genuineParts:
      entryData?.genuine_parts?.map((o) => ({
        isStartPrice: o?.is_start_price || false,
        isIsuzuGenuinePart: o?.is_isuzu_genuine_part || false,
        isTripetchGenuinePart: o?.is_tripetch_genuine_part || false,
        isBvpPart: o?.is_bvp_part || false,
        title: o?.title || '',
        price: o?.price || null,
        specialPrice: o?.special_price || null,
        unit: o?.unit || '',
      })) || [],
    replacementInterval: entryData?.replacement_interval || '',
    remark: entryData?.remark || '',
    isuzuSvcAdvantages: entryData?.isuzu_svc_advantages || '',
    symptomsChecker: entryData?.symptoms_checker || '',
    hasSymptomsRemark: entryData?.has_symptoms_remark || false,
    damages: entryData?.damages || '',
    damagesRemark: entryData?.damages_remark || '',
    warranty: {
      warrantyDescription: entryData?.warranty?.warranty_description || '',
      warrantyFile: { src: entryData?.warranty?.warranty_file?.url || '' },
      warrantyLink: entryData?.warranty?.warranty_link || '',
    },
    offer: {
      description: entryData?.offer?.description || '',
      url: entryData?.offer?.url || '',
    },
    batteryTable: {
      active: entryData?.battery_table?.active || false,
      items:
        entryData?.battery_table?.items?.map((o) => ({
          ampere: o?.ampere || '',
          model: o?.model || '',
          dmaxYear: o?.dmax_year || '',
          muxYear: o?.mux_year || '',
          mu7Year: o?.mu7_year || '',
          price: o?.price || null,
          specialPrice: o?.special_price || null,
        })) || [],
    },
    tireTable: {
      active: entryData?.tire_table?.active || false,
      items:
        entryData?.tire_table?.items?.map((o) => ({
          tire: o?.tire || '',
          size: o?.size || '',
          model: o?.model || '',
          vehicleModel: o?.vehicle_model || '',
          brand: o?.brand || '',
          price: o?.price || null,
          specialPrice: o?.special_price || null,
        })) || [],
    },
    downloadBrochureButton: {
      label: 'รายการอะไหล่แท้ตรีเพชรทั้งหมด',
      url: 'https://assets.contentstack.io/v3/assets/blt04fb7dc282801dd0/blt8fc953adc5cab748/lcv-parts-catalog',
      target: '_blank',
      icon: 'file-download',
      type: 'border',
      color: 'button-red',
      isMinWidth: true,
    },
  };

  const relatedRes = await getPartsByCategory(
    entry?.uid,
    entry?.category?.uid,
    'lcv'
  );
  const relatedEntry = relatedRes?.data?.entries?.map((item) => ({
    uid: item?.uid,
    title: item?.headline_title || '',
    vehicleType: item?.vehicle_type,
    url: item?.url || '',
    category: item?.category?.[0] && {
      uid: item.category[0]?.uid,
      name: item.category[0]?.name,
      url: item.category[0]?.url,
    },
    itemImage: { url: item?.item_image?.url || '' },
    genuineParts:
      item?.genuine_parts?.map((o) => ({
        isStartPrice: o?.is_start_price || false,
        isIsuzuGenuinePart: o?.is_isuzu_genuine_part || false,
        isTripetchGenuinePart: o.is_tripetch_genuine_part,
        isBvpPart: o?.is_bvp_part || false,
        title: o?.title || '',
        price: o?.price || null,
        specialPrice: o?.special_price || null,
        unit: o?.unit || '',
      })) || [],
  }));

  return {
    props: JSON.parse(
      JSON.stringify({ entry, relatedEntry: relatedEntry || [] })
    ),
  };
}

export default function Page({ entry, relatedEntry }) {
  const BREADCRUMB = [
    {
      label: 'อะไหล่สำหรับรถอีซูซุ',
      url: `${MENU_LCV_SERVICE_PARTS.url}?products=${entry.category.url || ''}`,
    },
    { label: entry?.title || '' },
  ];

  return (
    <>
      <CustomMetatag
        title={entry?.metatag?.title || entry?.title}
        description={entry?.metatag?.description || entry?.partDescription}
        imgURL={entry?.metatag?.imgURL || entry?.detailImage}
      />
      <main>
        <Breadcrumb items={BREADCRUMB} />
        <DescriptionTable detail={entry} />
        {entry?.category?.uid === BATTERY_TIRE &&
          !!entry?.batteryTable?.active &&
          entry?.batteryTable?.items?.length > 0 && <BatteryTable {...entry} />}
        {entry?.category?.uid === BATTERY_TIRE &&
          !!entry?.tireTable?.active &&
          entry?.tireTable?.items?.length > 0 && <TireTable {...entry} />}
        <SymptomsTable {...entry} />
        {relatedEntry?.length > 0 && (
          <RelatedParts
            items={relatedEntry}
            cardUrl={MENU_LCV_SERVICE_PARTS.url}
          />
        )}
      </main>
      <FooterLCVGroup />
    </>
  );
}

Page.getLayout = function getLayout(page: ReactElement) {
  return <Layout>{page}</Layout>;
};
